import React, {useState} from 'react';
import {useFormStore} from 'simple-creator/stores/formStore';
import {useFormContext} from 'simple-creator/contexts/FormContext';
import {useUser, useTranslations} from 'react-ui';
import {BasicInfoStep} from './steps/BasicInfoStep';
import {WhenWhereStep} from './steps/WhenWhereStep';
import {MediaStep} from './steps/MediaStep';
import {DetailsStep} from './steps/DetailsStep';
import {ReviewStep} from './steps/ReviewStep';
import {StepIndicator} from './common/StepIndicator';
import {FormFooter} from './common/FormFooter';
import {SaveStatusIndicator} from './common/SaveStatusIndicator';
import {usePublishEvent} from '../hooks/usePublishEvent';

const STEP_KEYS = [
    {id: 'basic', titleKey: 'sc_step_basic_info', component: BasicInfoStep},
    {id: 'when-where', titleKey: 'sc_step_when_where', component: WhenWhereStep},
    {id: 'media', titleKey: 'sc_step_media', component: MediaStep},
    {id: 'details', titleKey: 'sc_step_details', component: DetailsStep},
    {id: 'review', titleKey: 'sc_step_review', component: ReviewStep},
];

export const CreateEventForm: React.FC = () => {
    const {mode, callbacks, config, handleNextStep, handlePreviousStep, triggerSave: contextTriggerSave} = useFormContext();
    const {requiresUser, user} = useUser();
    const {translated} = useTranslations();
    const {
        formData,
        currentStep,
        stepValidation,
        saveStatus,
        lastSaved,
    } = useFormStore();

    // Publish hook - handles event publishing with proper API calls
    const { 
        isPublishing, 
        publishError,
        publishErrorCode,
        imageUploadErrors,
        publish, 
        clearError 
    } = usePublishEvent();

    const [showSuccess, setShowSuccess] = useState(false);
    
    const handleSubmit = async () => {
        clearError();
        const result = await publish();
        
        if (result.success) {
            setShowSuccess(true);
            // Call onPublish callback if provided
            console.log('[CreateEventForm] Publish success, calling onPublish with eventId:', result.eventId);
            callbacks.onPublish?.(result.eventId!);
            
            // Reset success state after delay (form will be closed by parent)
            setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        } else {
            callbacks.onError?.(new Error(result.error || 'Failed to publish event'));
        }
    };

    const handleCancel = () => {
        callbacks.onCancel?.();
    };

    const handlePublish = () => requiresUser({
        callback: handleSubmit,
    })
    const CurrentStepComponent = STEP_KEYS[currentStep].component;

    if (showSuccess) {
        return (
            <div className="w-full h-full flex flex-col items-center justify-center p-8 text-center">
                <div className="text-6xl mb-4 animate-celebrate">🎉</div>
                <h2 className="h2 mb-2">{mode === 'edit' ? translated('sc_event_updated') : translated('sc_event_published')}</h2>
                <p className="body text-gray-600">
                    Your event is now live on the map
                </p>
            </div>
        );
    }

    return (
        <div className="creator-form w-full h-full flex flex-col">
            {/* Debug: User Info (only show in debug mode) */}
            {config.debug && user && (
                <div className="mb-4 p-2 bg-blue-50 border border-blue-200 rounded text-xs shrink-0">
                    <span className="font-medium">🔐 Logged in as:</span> {user._name || user._email}
                    <button
                        onClick={() => {
                            localStorage.removeItem('token');
                            window.location.reload();
                        }}
                        className="ml-2 px-2 py-1 bg-blue-100 hover:bg-blue-200 rounded text-blue-700"
                    >
                        Logout
                    </button>
                </div>
            )}

            {/* Progress Header - Fixed */}
            <div className="mb-2 shrink-0 border-b border-gray-200">
                <StepIndicator
                    steps={STEP_KEYS.length}
                    currentStep={currentStep}
                    labels={STEP_KEYS.map(s => translated(s.titleKey))}
                />
                <div className="flex justify-end pb-1">
                    <SaveStatusIndicator 
                        status={saveStatus} 
                        lastSaved={lastSaved} 
                        onRetry={() => contextTriggerSave(true)}
                    />
                </div>
            </div>

            {/* Form Content - Scrollable */}
            <div className="flex-1 overflow-y-auto min-h-0">
                <CurrentStepComponent/>
            </div>

            {/* Publish Error Banner */}
            {publishError && (
              <div
                role="alert"
                className={`p-3 rounded-lg text-sm shrink-0 ${
                  publishErrorCode && publishErrorCode >= 400 && publishErrorCode < 500
                    ? 'bg-yellow-50 border border-yellow-200 text-yellow-800'
                    : 'bg-red-50 border border-red-200 text-red-800'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="shrink-0 mt-0.5">
                    {publishErrorCode && publishErrorCode >= 400 && publishErrorCode < 500 ? '⚠️' : '❌'}
                  </span>
                  <div>
                    <p className="font-medium">
                      {publishErrorCode && publishErrorCode >= 400 && publishErrorCode < 500
                        ? 'Validation Error'
                        : 'Something went wrong'}
                    </p>
                    <p className="mt-0.5">{publishError}</p>
                  </div>
                  <button
                    type="button"
                    onClick={clearError}
                    className="ml-auto shrink-0 p-1 hover:opacity-70 transition-opacity"
                    aria-label="Dismiss error"
                  >
                    ✕
                  </button>
                </div>
              </div>
            )}

            {/* Image Upload Warnings */}
            {imageUploadErrors.length > 0 && (
              <div
                role="alert"
                className="p-3 rounded-lg text-sm shrink-0 bg-yellow-50 border border-yellow-200 text-yellow-800"
              >
                <div className="flex items-start gap-2">
                  <span className="shrink-0 mt-0.5">⚠️</span>
                  <div>
                    <p className="font-medium">Some images failed to upload</p>
                    <ul className="mt-1 list-disc list-inside">
                      {imageUploadErrors.map((err, i) => (
                        <li key={i}>{err}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            )}

            {/* Navigation Footer - Fixed */}
            <FormFooter
                onCancel={currentStep > 0 ? handlePreviousStep : handleCancel}
                onContinue={currentStep < STEP_KEYS.length - 1 ? handleNextStep : handlePublish}
                cancelLabel={currentStep > 0 ? translated('sc_back') : translated('cancel')}
                continueLabel={currentStep < STEP_KEYS.length - 1 ? translated('sc_continue') : (mode === 'edit' ? translated('sc_update_event') : translated('sc_publish_event'))}
                continueDisabled={currentStep < STEP_KEYS.length - 1 ? !isStepValid(currentStep, formData, stepValidation) : isPublishing}
                isLoading={currentStep === STEP_KEYS.length - 1 && isPublishing}
                loadingLabel={translated('sc_publishing')}
            />
        </div>
    );
};

// Helper functions
function isStepValid(step: number, formData: any, stepValidation: Record<number, boolean>): boolean {
    console.log('+++++++++++++++isStepValid', step, formData, stepValidation);
    switch (step) {
        case 0: // Basic info - title, category, and description required
            return !!(formData.title && formData.categories.length > 0 && formData.description && formData.description.replace(/<[^>]*>/g, '').trim().length > 0);
        case 1: // When & Where
            return !!(formData.dates.length > 0 && formData.location.address && formData.location.coordinates.lat && formData.location.coordinates.lng);
        case 2: // Media (optional)
            return true;
        case 3: // Details (optional) - check stepValidation if set
            return stepValidation[3] !== false;
        case 4: // Review
            return true;
        default:
            return false;
    }
}

